import { useCallback } from 'react';
import { ScheduleItem } from '@app-types';

interface ApiMethods {
  updateOwners: (itemId: number | null, contractId: number, assigneeIds: number[]) => Promise<void>;
  updateManagers: (itemId: number | null, contractId: number, assigneeIds: number[]) => Promise<void>;
  updateAssessor: (item: ScheduleItem, assessorId: number, reason?: string) => Promise<void>;
  scheduleOrReschedule: (item: ScheduleItem, date: string, reason?: string, notes?: string) => Promise<void>;
  addToGroup: (groupId: number, contractIds: number[]) => Promise<void>;
  removeFromGroup: (groupId: number, contractId: number) => Promise<void>;
}

interface UseSchedulePageHandlersOptions {
  // From useSchedulePageModals
  selectedItem: ScheduleItem | null;
  selectedContractId: number | null;
  selectedIsGroupOperation: boolean;
  removeGroupId: number | null;
  removeContractId: number | null;
  openScheduledDateModal: (item: ScheduleItem) => void;
  closeScheduledDateModal: () => void;
  openAssessorModal: (item: ScheduleItem) => void;
  closeAssessorModal: () => void;
  openManagerModal: (item: ScheduleItem, contractId: number) => void;
  closeManagerModal: () => void;
  openOwnerModal: (item: ScheduleItem, contractId: number, isGroupOperation?: boolean) => void;
  closeOwnerModal: () => void;
  openAddToGroupModal: (item: ScheduleItem) => void;
  closeAddToGroupModal: () => void;
  openRemoveConfirm: (groupId: number, contractId: number) => void;
  closeRemoveConfirm: () => void;

  // From useSchedulePageData
  refreshData: () => void;

  // API methods (different for Assessor/Scheduler)
  apiMethods: ApiMethods;

  // Optional callbacks for additional logic
  onAddToGroupSuccess?: () => void;
  onRemoveFromGroupSuccess?: () => void;
}

export interface UseSchedulePageHandlersReturn {
  // Generic role assignment handler (for table)
  handleAssignRole: (
    item: ScheduleItem,
    contractId: number,
    role: 'owner' | 'manager' | 'assessor',
    isGroupOperation?: boolean
  ) => void;

  // Specific role handlers (for backwards compatibility if needed)
  handleScheduleDate: (item: ScheduleItem) => void;
  handleReassignAssessor: (item: ScheduleItem) => void;
  handleAssignManager: (item: ScheduleItem, contractId?: number) => void;
  handleAssignOwner: (item: ScheduleItem, contractId?: number, isGroupOperation?: boolean) => void;

  // Modal submission handlers
  handleDateSelected: (date: string, reason?: string, notes?: string) => Promise<void>;
  handleAssessorAssigned: (assessorId: number, reason?: string) => Promise<void>;
  handleManagerAssigned: (assigneeIds: number[]) => Promise<void>;
  handleOwnerAssigned: (assigneeIds: number[]) => Promise<void>;

  // Group management handlers
  handleAddToGroup: (item: ScheduleItem) => void;
  handleAddContractsToGroup: (groupId: number, contractIds: number[]) => Promise<void>;
  handleRemoveFromGroup: (groupId: number, contractId: number) => void;
  confirmRemoveFromGroup: () => Promise<void>;
}

/**
 * Generic hook for schedule page handlers (Scheduler & Assessor)
 * Handles: role assignments, date scheduling, group management
 */
export function useSchedulePageHandlers(options: UseSchedulePageHandlersOptions): UseSchedulePageHandlersReturn {
  const {
    selectedItem,
    selectedContractId,
    selectedIsGroupOperation,
    removeGroupId,
    removeContractId,
    openScheduledDateModal,
    closeScheduledDateModal,
    openAssessorModal,
    closeAssessorModal,
    openManagerModal,
    closeManagerModal,
    openOwnerModal,
    closeOwnerModal,
    openAddToGroupModal,
    closeAddToGroupModal,
    openRemoveConfirm,
    closeRemoveConfirm,
    refreshData,
    apiMethods,
    onAddToGroupSuccess,
    onRemoveFromGroupSuccess
  } = options;

  // Schedule/Reschedule Date
  const handleScheduleDate = useCallback(
    (item: ScheduleItem) => {
      openScheduledDateModal(item);
    },
    [openScheduledDateModal]
  );

  const handleDateSelected = useCallback(
    async (date: string, reason?: string, notes?: string) => {
      if (!selectedItem) return;

      try {
        await apiMethods.scheduleOrReschedule(selectedItem, date, reason, notes);
        refreshData();
        closeScheduledDateModal();
      } catch (error) {
        console.error('Failed to schedule/reschedule:', error);
        throw error; // Re-throw to let modal handle the error state
      }
    },
    [selectedItem, apiMethods, refreshData, closeScheduledDateModal]
  );

  // Assessor Assignment
  const handleReassignAssessor = useCallback(
    (item: ScheduleItem) => {
      openAssessorModal(item);
    },
    [openAssessorModal]
  );

  const handleAssessorAssigned = useCallback(
    async (assessorId: number, reason: string = '') => {
      if (!selectedItem) return;

      try {
        await apiMethods.updateAssessor(selectedItem, assessorId, reason);
        refreshData();
        closeAssessorModal();
      } catch (error) {
        console.error('Failed to assign assessor:', error);
        throw error;
      }
    },
    [selectedItem, apiMethods, refreshData, closeAssessorModal]
  );

  // Manager Assignment
  const handleAssignManager = useCallback(
    (item: ScheduleItem, contractId?: number) => {
      openManagerModal(item, contractId || item.contracts[0]?.id);
    },
    [openManagerModal]
  );

  const handleManagerAssigned = useCallback(
    async (assigneeIds: number[]) => {
      if (!selectedItem || !selectedContractId) return;

      try {
        await apiMethods.updateManagers(selectedItem.id, selectedContractId, assigneeIds);
        refreshData();
        closeManagerModal();
      } catch (error) {
        console.error('Failed to assign managers:', error);
        throw error;
      }
    },
    [selectedItem, selectedContractId, apiMethods, refreshData, closeManagerModal]
  );

  // Owner Assignment
  const handleAssignOwner = useCallback(
    (item: ScheduleItem, contractId?: number, isGroupOperation?: boolean) => {
      const finalContractId = contractId || item.contracts[0]?.id || 0;
      openOwnerModal(item, finalContractId, isGroupOperation);
    },
    [openOwnerModal]
  );

  const handleOwnerAssigned = useCallback(
    async (assigneeIds: number[]) => {
      if (!selectedItem || !selectedContractId) return;

      try {
        // Use selectedIsGroupOperation to determine if updating group or single contract
        const itemId = selectedIsGroupOperation ? selectedItem.id : null;

        await apiMethods.updateOwners(itemId, selectedContractId, assigneeIds);
        refreshData();
        closeOwnerModal();
      } catch (error) {
        console.error('Failed to assign owners:', error);
        throw error;
      }
    },
    [selectedItem, selectedContractId, selectedIsGroupOperation, apiMethods, refreshData, closeOwnerModal]
  );

  // Add to Group
  const handleAddToGroup = useCallback(
    (item: ScheduleItem) => {
      openAddToGroupModal(item);
    },
    [openAddToGroupModal]
  );

  const handleAddContractsToGroup = useCallback(
    async (groupId: number, contractIds: number[]) => {
      try {
        await apiMethods.addToGroup(groupId, contractIds);
        refreshData();
        closeAddToGroupModal();
        onAddToGroupSuccess?.();
      } catch (error) {
        console.error('Failed to add to group:', error);
        onAddToGroupSuccess?.(); // Clear selection even on error
        throw error;
      }
    },
    [apiMethods, refreshData, closeAddToGroupModal, onAddToGroupSuccess]
  );

  // Remove from Group
  const handleRemoveFromGroup = useCallback(
    (groupId: number, contractId: number) => {
      openRemoveConfirm(groupId, contractId);
    },
    [openRemoveConfirm]
  );

  const confirmRemoveFromGroup = useCallback(async () => {
    if (removeGroupId === null || removeContractId === null) {
      closeRemoveConfirm();
      return;
    }

    try {
      await apiMethods.removeFromGroup(removeGroupId, removeContractId);
      refreshData();
      onRemoveFromGroupSuccess?.();
    } catch (error) {
      console.error('Failed to remove from group:', error);
      onRemoveFromGroupSuccess?.(); // Clear selection even on error
    } finally {
      closeRemoveConfirm();
    }
  }, [removeGroupId, removeContractId, apiMethods, refreshData, closeRemoveConfirm, onRemoveFromGroupSuccess]);

  // Generic role assignment handler (unified API for tables)
  const handleAssignRole = useCallback(
    (item: ScheduleItem, contractId: number, role: 'owner' | 'manager' | 'assessor', isGroupOperation?: boolean) => {
      switch (role) {
        case 'owner':
          handleAssignOwner(item, contractId, isGroupOperation);
          break;
        case 'manager':
          handleAssignManager(item, contractId);
          break;
        case 'assessor':
          handleReassignAssessor(item);
          break;
      }
    },
    [handleAssignOwner, handleAssignManager, handleReassignAssessor]
  );

  return {
    // Generic role assignment handler (for table)
    handleAssignRole,

    // Specific role handlers
    handleScheduleDate,
    handleReassignAssessor,
    handleAssignManager,
    handleAssignOwner,

    // Modal submission handlers
    handleDateSelected,
    handleAssessorAssigned,
    handleManagerAssigned,
    handleOwnerAssigned,

    // Group management handlers
    handleAddToGroup,
    handleAddContractsToGroup,
    handleRemoveFromGroup,
    confirmRemoveFromGroup
  };
}
